import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-compare-view>` says about itself.
 *
 * The agreement vocabulary is the point: In both, different · In both, equal · Only in the source ·
 * Only in the target. Four phrases saying precisely what a comparison of two things can produce,
 * with nothing left out. It is carried verbatim rather than compressed into "changed / added /
 * removed", which loses the difference between "the same" and "not on the other side at all".
 *
 * Equal items are hidden by default — a comparison returns thousands and almost all agree — and the
 * summary ALWAYS says how many were hidden, so nobody mistakes the short list for the comparison.
 * The counts are over the whole comparison rather than the filtered view, because what somebody
 * needs from the top line is how big the difference is.
 */
const docs: ComponentDocs = {
  aliases: ['org compare', 'metadata diff', 'comparison results', 'environment differences', 'what changed'],

  answers: [
    'see what differs between two environments',
    'find what exists in one environment and not the other',
    'narrow a comparison to one metadata type',
    'open a side-by-side difference for one item',
  ],

  placements: ['appPage'],

  whenToUse: 'After comparing two environments, when the question is WHICH things disagree.',

  insteadUse: [
    { name: 'DifferenceModal', when: 'one item has been chosen and the question is HOW it differs.' },
    { name: 'EnvironmentConnector', when: 'the two environments have not been chosen yet.' },
  ],

  inputs: [
    {
      name: 'items',
      type: 'json',
      description: 'What was compared: `{ type, name, agreement }[]`.',
      default: [],
    },
    {
      name: 'agreement',
      attribute: 'agreement',
      type: 'text',
      options: ['both-different', 'both-equal', 'only-source', 'only-target'],
      description:
        'Show only one of the four answers. Choosing `both-equal` reveals the equal items even ' +
        'without `show-equal`, because asking for them is asking for them.',
      fallback: 'everything that differs, when the attribute is absent or empty',
    },
    {
      name: 'showEqual',
      attribute: 'show-equal',
      type: 'boolean',
      description:
        'Include the items that agree. Off by default: a comparison returns thousands and almost all ' +
        'agree, so leading with them buries the answer.',
      default: false,
    },
    {
      name: 'type',
      attribute: 'type',
      type: 'text',
      description: 'Show only one metadata type. The filter offers the types actually present.',
      fallback: 'every type, when the attribute is absent or empty',
    },
    {
      name: 'search',
      attribute: 'search',
      type: 'text',
      description: 'Narrows by item name or type. Intersects with the other two filters.',
    },
    {
      name: 'sortKey',
      attribute: 'sort-key',
      type: 'text',
      options: ['type', 'name', 'agreement'],
      description: 'Which column orders the rows.',
      default: 'name',
    },
    {
      name: 'sortDirection',
      attribute: 'sort-direction',
      type: 'text',
      options: ['asc', 'desc'],
      description: 'Which way.',
      default: 'asc',
    },
    {
      name: 'emptyText',
      attribute: 'empty-text',
      type: 'text',
      description: 'What to say before anything has been compared.',
      default: 'Nothing has been compared yet.',
    },
  ],

  events: [
    {
      name: 'caos-compare-open',
      when: 'A row’s Compare control is pressed. Only rows that differ have one.',
      carries: '`{ item }`.',
    },
    { name: 'caos-compare-filter', when: 'The type or agreement filter changes.', carries: '`{ type, agreement }`.' },
  ],

  state: [
    {
      name: 'tally',
      type: 'Record<CaosAgreement, number>',
      description: 'How many of each answer, over the WHOLE comparison — never the filtered view.',
    },
    { name: 'differing', type: 'CaosCompareItem[]', description: 'Everything that is not "in both, equal".' },
    { name: 'types', type: 'string[]', description: 'The distinct types compared.' },
    { name: 'visibleItems', type: 'CaosCompareItem[]', description: 'The items surviving every filter, in order.' },
  ],

  parts: [
    { part: 'compare', is: 'The outer column.' },
    { part: 'filters', is: 'The filter row.' },
    { part: 'filter', is: 'One of the two dropdowns.' },
    { part: 'search', is: 'The search box.' },
    { part: 'summary', is: 'The counts line.' },
    { part: 'table', is: 'The composed data table.' },
    { part: 'agreement', is: 'A row’s comparison phrase.' },
    { part: 'open', is: 'A row’s Compare control.', when: 'That row differs.' },
  ],

  accessibility:
    'Every row states its comparison as a full PHRASE — "Only in the source" — rather than as a ' +
    'colour or an abbreviation, so the four outcomes are distinguishable when read aloud. The ' +
    'summary is a `role="status"` live region and always states how many equal items are hidden, so ' +
    'the short list is never mistaken for the whole comparison. Each Compare control names the item ' +
    'it opens, which matters on a screen holding forty identical controls.',

  examples: [
    {
      key: 'differences',
      label: 'What disagrees',
      summary:
        'The default view: the equal items are hidden and the summary says how many, so the short ' +
        'list cannot be mistaken for the comparison.',
      placement: {
        component: 'caos-compare-view',
        inputs: {
          items: [
            { type: 'ApexClass', name: 'BillingService', agreement: 'both-different' },
            { type: 'ApexClass', name: 'QuoteCalculator', agreement: 'both-equal' },
            { type: 'ApexClass', name: 'LegacyImporter', agreement: 'only-source' },
            { type: 'CustomField', name: 'Account.Region__c', agreement: 'only-target' },
            { type: 'CustomField', name: 'Account.Industry', agreement: 'both-equal' },
            { type: 'Layout', name: 'Account-Account Layout', agreement: 'both-different' },
          ],
        },
      },
      stage: { needsWidth: 820 },
    },
    {
      key: 'one-answer',
      label: 'Only what is missing from the target',
      summary:
        'One of the four phrases on its own. "Only in the source" says exactly what it means, where ' +
        '"removed" would have implied somebody took it away.',
      placement: {
        component: 'caos-compare-view',
        inputs: {
          agreement: 'only-source',
          items: [
            { type: 'ApexClass', name: 'BillingService', agreement: 'both-different' },
            { type: 'ApexClass', name: 'LegacyImporter', agreement: 'only-source' },
            { type: 'ApexTrigger', name: 'AccountTrigger', agreement: 'only-source' },
            { type: 'CustomField', name: 'Account.Region__c', agreement: 'only-target' },
          ],
        },
      },
      stage: { needsWidth: 820 },
    },
    {
      key: 'equal-shown',
      label: 'Everything, agreements included',
      summary: 'The equal items brought back in, marked as the quiet rows they are and with no Compare control.',
      placement: {
        component: 'caos-compare-view',
        inputs: {
          'show-equal': true,
          items: [
            { type: 'ApexClass', name: 'BillingService', agreement: 'both-different' },
            { type: 'ApexClass', name: 'QuoteCalculator', agreement: 'both-equal' },
            { type: 'CustomField', name: 'Account.Industry', agreement: 'both-equal' },
          ],
        },
      },
      stage: { needsWidth: 820 },
    },
  ],
};

export default docs;
